import { computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { RealtimeService } from '../../../core/services/realtime.service';
import { ToastService } from '../../../core/services/toast.service';
import {
  FILTER_STORAGE_KEYS,
  FilterSelectionStorageService,
} from '../../../core/services/filter-selection-storage.service';
import { MatchApiService } from '../../../domain/api/match-api.service';
import {
  Match,
  MatchEvent,
  MatchFilterOptions,
  MatchFilters,
  MatchStatut,
  MatchSuggestion,
  MatchTotals,
} from '../../../domain/models';

export type MatchTab =
  | 'suggestions'
  | 'en-cours'
  | 'accepte'
  | 'refuse'
  | 'contrats';

const EMPTY_TOTALS: MatchTotals = {
  total: 0,
  enCours: 0,
  entretien: 0,
  accepte: 0,
  refuse: 0,
  contrat: 0,
  suggestions: 0,
};

const EMPTY_OPTIONS: MatchFilterOptions = {
  entreprises: [],
  apprenants: [],
  conseillers: [],
  offres: [],
};

const TAB_FILTERS: Record<
  Exclude<MatchTab, 'suggestions'>,
  Partial<MatchFilters>
> = {
  'en-cours': { statut: 'En cours' },
  accepte: { statut: 'Accepté' },
  refuse: { statut: 'Refusé' },
  contrats: { avecContrat: true },
};

interface MatchingState {
  matches: Match[];
  suggestions: MatchSuggestion[];
  totals: MatchTotals;
  page: number;
  limit: number;
  total: number;
  suggestionTotal: number;
  filters: MatchFilters;
  options: MatchFilterOptions;
  loading: boolean;
  initialLoad: boolean;
  activeTab: MatchTab;
  selectedId: string | null;
}

const initialState: MatchingState = {
  matches: [],
  suggestions: [],
  totals: EMPTY_TOTALS,
  page: 0,
  limit: 10,
  total: 0,
  suggestionTotal: 0,
  filters: {},
  options: EMPTY_OPTIONS,
  loading: true,
  initialLoad: true,
  activeTab: 'en-cours',
  selectedId: null,
};

export const MatchingStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ matches, suggestions, activeTab, selectedId, totals, total, suggestionTotal }) => ({
    inProgressCount: computed(() => totals().enCours),
    selected: computed(() => {
      if (activeTab() === 'suggestions') {
        return (
          suggestions().find((s) => s.id === selectedId()) ?? null
        );
      }
      return matches().find((m) => m.id === selectedId()) ?? null;
    }),
    showSuggestions: computed(() => activeTab() === 'suggestions'),
    listTotal: computed(() =>
      activeTab() === 'suggestions' ? suggestionTotal() : total(),
    ),
  })),
  withMethods((
    store,
    api = inject(MatchApiService),
    toast = inject(ToastService),
    filterStorage = inject(FilterSelectionStorageService),
  ) => ({
    load(page = store.page(), limit = store.limit()): void {
      if (store.activeTab() === 'suggestions') {
        this.loadSuggestions(page, limit);
        return;
      }

      patchState(store, { loading: true });
      const tabFilters = TAB_FILTERS[store.activeTab() as Exclude<MatchTab, 'suggestions'>] ?? {};
      api
        .list({
          page,
          limit,
          ...store.filters(),
          ...tabFilters,
        })
        .subscribe({
          next: (response) => {
            patchState(store, {
              matches: response.matchings,
              total: response.total,
              page: response.page,
              limit: response.limit,
              totals: response.totals,
              loading: false,
              initialLoad: false,
            });
          },
          error: () => {
            toast.error('Échec du chargement des matchings.');
            patchState(store, { loading: false, initialLoad: false });
          },
        });
    },
    loadSuggestions(page = store.page(), limit = store.limit()): void {
      patchState(store, { loading: true });
      api
        .listSuggestions({
          page,
          limit,
          ...store.filters(),
        })
        .subscribe({
          next: (response) => {
            patchState(store, {
              suggestions: response.suggestions,
              suggestionTotal: response.total,
              page: response.page,
              limit: response.limit,
              loading: false,
              initialLoad: false,
            });
            this.refreshTotals();
          },
          error: () => {
            toast.error('Échec du chargement des suggestions.');
            patchState(store, { loading: false, initialLoad: false });
          },
        });
    },
    loadFilterOptions(): void {
      api.filterOptions().subscribe({
        next: (options) => patchState(store, { options }),
        error: () => {},
      });
    },
    refreshTotals(): void {
      api.list({ page: 0, limit: 1, ...store.filters() }).subscribe({
        next: (response) => patchState(store, { totals: response.totals }),
        error: () => {},
      });
    },
    setTab(tab: MatchTab): void {
      patchState(store, { activeTab: tab, page: 0, selectedId: null });
      this.load(0, store.limit());
    },
    select(id: string): void {
      patchState(store, { selectedId: id });
    },
    closePanel(): void {
      patchState(store, { selectedId: null });
    },
    setFilters(filters: MatchFilters): void {
      if (filters.conseillerIds !== undefined) {
        filterStorage.writeIds(
          FILTER_STORAGE_KEYS.matchingConseillers,
          filters.conseillerIds,
        );
      }
      const next = { ...filters };
      if (next.conseillerIds?.length) {
        delete next.conseillerId;
      }
      patchState(store, { filters: next, page: 0, selectedId: null });
      this.load(0, store.limit());
    },
    updateFilters(partial: Partial<MatchFilters>): void {
      const merged = { ...store.filters(), ...partial };
      if (partial.conseillerIds !== undefined) {
        delete merged.conseillerId;
      }
      this.setFilters(merged);
    },
    resetFilters(): void {
      filterStorage.writeIds(FILTER_STORAGE_KEYS.matchingConseillers, []);
      this.setFilters({});
    },
    setPage(page: number): void {
      patchState(store, { page, selectedId: null });
      this.load(page, store.limit());
    },
    setLimit(limit: number): void {
      patchState(store, { limit, page: 0, selectedId: null });
      this.load(0, limit);
    },
    applyOffreFilter(offreId: string): void {
      this.setTab('en-cours');
      this.setFilters({ offreId });
    },
    updateStatut(id: string, statut: MatchStatut, remarque?: string): void {
      api.updateStatut(id, statut, remarque).subscribe({
        next: () => {
          toast.success('Statut mis à jour.');
          this.load();
          this.refreshTotals();
        },
        error: () => toast.error('Échec de la mise à jour du statut.'),
      });
    },
    remove(id: string): void {
      api.remove(id).subscribe({
        next: () => {
          toast.success('Matching annulé.');
          patchState(store, {
            selectedId: store.selectedId() === id ? null : store.selectedId(),
          });
          this.load();
          this.refreshTotals();
        },
        error: () => toast.error('Échec de la suppression du matching.'),
      });
    },
    acceptSuggestion(id: string): void {
      api.acceptSuggestion(id).subscribe({
        next: () => {
          toast.success('Suggestion acceptée.');
          patchState(store, { selectedId: null });
          this.loadSuggestions();
          this.refreshTotals();
        },
        error: () => toast.error("Échec de l'acceptation de la suggestion."),
      });
    },
    dismissSuggestion(id: string): void {
      api.dismissSuggestion(id).subscribe({
        next: () => {
          toast.success('Suggestion ignorée.');
          patchState(store, {
            selectedId: store.selectedId() === id ? null : store.selectedId(),
          });
          this.loadSuggestions();
          this.refreshTotals();
        },
        error: () => toast.error("Échec de l'ignorance de la suggestion."),
      });
    },
    applyEvent(event: MatchEvent): void {
      const current = store.matches();
      if (event.type === 'removed') {
        patchState(store, {
          matches: current.filter((m) => m.id !== event.match.id),
        });
        return;
      }
      const exists = current.some((m) => m.id === event.match.id);
      patchState(store, {
        matches: exists
          ? current.map((m) =>
              m.id === event.match.id ? event.match : m,
            )
          : [event.match, ...current],
      });
    },
  })),
  withHooks({
    onInit(
      store,
      realtime = inject(RealtimeService),
      filterStorage = inject(FilterSelectionStorageService),
    ) {
      const saved = filterStorage.readIds(FILTER_STORAGE_KEYS.matchingConseillers);
      if (saved.length) {
        patchState(store, { filters: { conseillerIds: saved } });
      }
      store.load();
      store.loadFilterOptions();
      realtime
        .on<MatchEvent>('match:event')
        .pipe(takeUntilDestroyed())
        .subscribe((event) => store.applyEvent(event));
    },
  }),
);
